import { setup } from 'xstate';
import { compareFinalisedVersions, type FinalisedVersion } from './finalised-version.js';

export type TrialLifecycleContext = {
  readonly ready: number;
  readonly total: number;
  readonly packComplete: boolean;
  readonly latestFinalisedVersion: FinalisedVersion | null;
};

export type TrialLifecycleEvent =
  | { readonly type: 'START_GENERATION' }
  | { readonly type: 'GENERATION_SUCCEEDED' }
  | { readonly type: 'GENERATION_FAILED' }
  | { readonly type: 'FINALISE'; readonly version: FinalisedVersion }
  | { readonly type: 'SCRIPT_CHANGED' };

const readinessHolds = (context: TrialLifecycleContext): boolean => context.ready === context.total;

const versionStrictlyGreater = (
  context: TrialLifecycleContext,
  version: FinalisedVersion,
): boolean =>
  context.latestFinalisedVersion === null ||
  compareFinalisedVersions(version, context.latestFinalisedVersion) > 0;

export const trialLifecycleMachine = setup({
  types: {} as {
    context: TrialLifecycleContext;
    events: TrialLifecycleEvent;
  },
  guards: {
    packComplete: ({ context }) => context.packComplete,
    finaliseAllowed: ({ context }, params: { readonly version: FinalisedVersion }) =>
      readinessHolds(context) && versionStrictlyGreater(context, params.version),
  },
}).createMachine({
  id: 'trialLifecycle',
  initial: 'setting_up',
  context: {
    ready: 0,
    total: 0,
    packComplete: false,
    latestFinalisedVersion: null,
  },
  states: {
    setting_up: {
      on: {
        START_GENERATION: { target: 'generating', guard: 'packComplete' },
      },
    },
    generating: {
      on: {
        GENERATION_SUCCEEDED: { target: 'in_review' },
        GENERATION_FAILED: { target: 'failed' },
      },
    },
    in_review: {
      on: {
        FINALISE: {
          target: 'finalised',
          guard: {
            type: 'finaliseAllowed',
            params: ({ event }) => ({ version: event.version }),
          },
        },
      },
    },
    finalised: {
      on: {
        SCRIPT_CHANGED: { target: 'in_review' },
      },
    },
    failed: {
      on: {
        START_GENERATION: { target: 'generating', guard: 'packComplete' },
      },
    },
  },
});
